import React, { useContext, useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase/firebase-config';
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  deleteUser as deleteAuthUser
} from 'firebase/auth';
import {
  doc,
  getDoc,
  onSnapshot,
  writeBatch,
  updateDoc,
  serverTimestamp,
  collection,
  increment,
  deleteDoc,
  Timestamp,
  addDoc
} from 'firebase/firestore';

const AuthClienteContext = React.createContext();

export function useAuthCliente() {
  return useContext(AuthClienteContext);
}

export function AuthClienteProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const profileUnsub = useRef(null);
  const companyProfileUnsub = useRef(null);

  const loadUserProfileAndCompany = async (user, companyId) => {
    try {
      const userDocRef = doc(db, 'companies', companyId, 'end_customers', user.uid);
      const companyDocRef = doc(db, 'companies', companyId);
      const profileRef = doc(db, 'client_profiles', user.uid);

      const [userSnap, companySnap, profileSnap] = await Promise.all([
        getDoc(userDocRef),
        getDoc(companyDocRef),
        getDoc(profileRef)
      ]);

      if (!userSnap.exists()) {
        throw new Error("Perfil de cliente no encontrado en esta empresa.");
      }

      const userData = userSnap.data();
      const companyData = companySnap.exists() ? companySnap.data() : {};
      const profileData = profileSnap.exists() ? profileSnap.data() : { memberships: [] };

      const fullUserProfile = {
        providerId: user.providerId,
        uid: user.uid,
        email: user.email,
        ...userData,
        memberships: profileData.memberships || [],
        companyId,
        companyName: companyData.name || 'Club sin nombre',
        contactPhone: companyData.contactPhone || null
      };

      setCurrentUser(fullUserProfile);
      return fullUserProfile;
    } catch (error) {
      console.error("Error crítico al cargar perfil:", error);
      await signOut(auth);
      setCurrentUser(null);
      throw error;
    }
  };

  async function signup(email, password, firstName, lastName, invitationToken) {
    let newUser = null;
    try {
      const functions = getFunctions();
      const validateInvitation = httpsCallable(functions, 'validateInvitationToken');
      const result = await validateInvitation({ token: invitationToken });
      const { companyId, invitationDocId } = result.data;

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      newUser = userCredential.user;

      const batch = writeBatch(db);

      const userDocRef = doc(db, 'companies', companyId, 'end_customers', newUser.uid);
      batch.set(userDocRef, {
        name: `${firstName} ${lastName}`,
        email,
        bookingTokens: 0,
        points: 0,
        authUid: newUser.uid,
        createdAt: serverTimestamp()
      });

      const directoryRef = doc(db, 'user_directory', email);
      batch.set(directoryRef, { companyId, authUid: newUser.uid }, { merge: true });

      const globalProfileRef = doc(db, 'client_profiles', newUser.uid);
      batch.set(globalProfileRef, { memberships: [companyId] }, { merge: true });

      const tokenRef = doc(db, 'companies', companyId, 'customer_invitations', invitationDocId);
      batch.update(tokenRef, {
        status: 'claimed',
        claimedBy: newUser.uid,
        claimedAt: serverTimestamp()
      });

      await batch.commit();
      
      localStorage.setItem('activeCompanyId', companyId);

      return { userCredential, companyId };
    } catch (error) {
      console.error("ERROR DETALLADO DE SIGNUP:", error);
      if (newUser) {
        await deleteAuthUser(newUser).catch(e =>
          console.error("Error al eliminar usuario huérfano:", e)
        );
      }
      throw error;
    }
  }

  async function login(email, password, companyId) {
    if (!companyId) {
      throw new Error("ID del club no proporcionado.");
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await loadUserProfileAndCompany(userCredential.user, companyId);
      localStorage.setItem('activeCompanyId', companyId);
      setLoginSuccess(true);
      return userCredential;
    } catch (error) {
      if (
        error.code === 'auth/user-not-found' ||
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/invalid-credential'
      ) {
        throw new Error("Error al iniciar sesión. Verifica tus credenciales.");
      }
      throw error;
    }
  }

  function logout() {
    setIsLoggingOut(true);
  }

  const finalizaLogout = () => {
    localStorage.removeItem('activeCompanyId');
    setLoginSuccess(false);
    setCurrentUser(null);
    signOut(auth).then(() => {
      setIsLoggingOut(false);
    });
  };

  const clearLoginSuccess = () => {
    setLoginSuccess(false);
  };

  useEffect(() => {
  const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
    if (profileUnsub.current) profileUnsub.current();
    if (companyProfileUnsub.current) companyProfileUnsub.current();

    console.log('[Auth] Estado de sesión detectado:', user);

    if (user) {
      setLoading(true);

      let companyId = localStorage.getItem('activeCompanyId');
      console.log('[Auth] companyId encontrado en localStorage:', companyId);

      // 🔍 Si no está en localStorage, lo buscamos en Firestore
      if (!companyId) {
        try {
          const directoryRef = doc(db, 'user_directory', user.email);
          const directorySnap = await getDoc(directoryRef);
          if (directorySnap.exists()) {
            companyId = directorySnap.data().companyId;
            localStorage.setItem('activeCompanyId', companyId);
            console.log('[Auth] companyId recuperado desde Firestore:', companyId);
          }
        } catch (err) {
          console.error('[Auth] Error buscando companyId en user_directory:', err);
        }
      }

      if (!companyId) {
        console.warn('[Auth] Sin companyId activo. Cerrando sesión forzada.');
        await signOut(auth);
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      try {
        console.log('[Auth] Intentando cargar perfil completo...');
        await loadUserProfileAndCompany(user, companyId);
        console.log('[Auth] Perfil cargado con éxito');
      } catch (error) {
        console.error('[Auth] Error crítico al cargar perfil. Forzando logout.', error);
        await signOut(auth);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    } else {
      console.log('[Auth] No hay usuario autenticado');
      setCurrentUser(null);
      setLoading(false);
    }
  });

  return () => {
    unsubscribeAuth();
    if (profileUnsub.current) profileUnsub.current();
    if (companyProfileUnsub.current) companyProfileUnsub.current();
  };
}, []);


  // Crear reserva
  async function createBooking(bookingDetails) {
    const { companyId, slotsToBook } = bookingDetails;
    if (!currentUser || currentUser.bookingTokens < slotsToBook.length) {
      throw new Error("No tienes suficientes tokens para esta reserva.");
    }
    const batch = writeBatch(db);
    slotsToBook.forEach(slot => {
      const newBookingRef = doc(collection(db, 'companies', companyId, 'bookings'));
      const bookingTimestamp = new Date(`${slot.date}T${slot.startTime}`);
      batch.set(newBookingRef, {
        userId: currentUser.uid,
        userName: currentUser.name,
        resourceId: slot.resource.id,
        resourceName: slot.resource.name,
        date: slot.date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        createdAt: serverTimestamp(),
        bookingTimestamp: Timestamp.fromDate(bookingTimestamp)
      });
    });
    const userProfileRef = doc(db, 'companies', companyId, 'end_customers', currentUser.uid);
    batch.update(userProfileRef, {
      bookingTokens: increment(-slotsToBook.length)
    });
    await batch.commit();
    setCurrentUser(prevUser => ({
      ...prevUser,
      bookingTokens: prevUser.bookingTokens - slotsToBook.length
    }));
  }

  // Cancelar reserva
  async function cancelBooking(bookingId) {
    if (!currentUser || !currentUser.companyId) {
      throw new Error("No estás autenticado.");
    }
    const bookingRef = doc(db, 'companies', currentUser.companyId, 'bookings', bookingId);
    const userProfileRef = doc(db, 'companies', currentUser.companyId, 'end_customers', currentUser.uid);
    const batch = writeBatch(db);
    batch.delete(bookingRef);
    batch.update(userProfileRef, {
      bookingTokens: increment(1)
    });
    await batch.commit();
    setCurrentUser(prevUser => ({
      ...prevUser,
      bookingTokens: prevUser.bookingTokens + 1
    }));
  }

  // Solicitar membresía a otro club
  async function requestMembership(targetCompanyId, requestMessage) {
    if (!currentUser) {
      throw new Error("Debes iniciar sesión para solicitar unirte a un club.");
    }
    const requestData = {
      userId: currentUser.uid,
      userName: currentUser.name,
      userEmail: currentUser.email,
      requestMessage,
      status: 'pending',
      requestedAt: serverTimestamp()
    };
    const requestsRef = collection(db, 'companies', targetCompanyId, 'membership_requests');
    await addDoc(requestsRef, requestData);
  }

  // Refrescar manualmente
  const refreshCurrentUser = async () => {
    if (currentUser?.uid && currentUser?.companyId) {
      await loadUserProfileAndCompany(currentUser, currentUser.companyId);
    }
  };

  const value = {
    currentUser,
    setCurrentUser,
    loading,
    db,
    login,
    signup,
    logout,
    loginSuccess,
    setLoginSuccess,
    clearLoginSuccess,
    createBooking,
    cancelBooking,
    refreshCurrentUser,
    isLoggingOut,
    finalizaLogout,
    requestMembership
  };

  return (
    <AuthClienteContext.Provider value={value}>
      {!loading && children}
    </AuthClienteContext.Provider>
  );
}